import Countries from 'i18n-iso-countries';
import { merge, omit } from 'lodash-es';
import type { FormatNumberOptions, IntlShape } from 'react-intl';
import * as z from 'zod';
import regionCodeMapping from 'resources/json/RegionCodes.json';
import sanctionedUnits from 'resources/json/sanctionedUnits.json';
import { type RegionCode } from 'types/models';

export * from './intl.lang';
export * from './intl.units';

export const countries = Object.keys(Countries.getAlpha2Codes());

export function loadZodLocale(locale: string) {
   const locales: Record<string, any> = (z as any).locales ?? {};
   const code = (locale ?? 'en').toLowerCase();
   const zodLocaleFunction = locales[code] ?? locales[code.slice(0, 2)] ?? locales.en;

   z.config(zodLocaleFunction());
}

export function formatRegionCode(intl: IntlShape, code: RegionCode) {
   // Handle undefined or null code
   if (!code) {
      return '';
   }

   let formattedCode;

   let [sanitizedCode] = code.split('-');
   [sanitizedCode] = sanitizedCode.split('_');

   if (sanitizedCode === 'UK') {
      sanitizedCode = 'GB';
   }

   const regularIntl = new Intl.DisplayNames([intl.locale], { type: 'region', style: 'long' });

   try {
      regularIntl.of(sanitizedCode);

      formattedCode = intl.formatDisplayName(sanitizedCode, {
         type: 'region',
         style: 'long',
      });
   } catch {
      formattedCode = intl.formatMessage({
         id: `emissions.regions.${code}`,
         defaultMessage: regionCodeMapping?.[code as keyof typeof regionCodeMapping] ?? code,
      });
   }

   return formattedCode;
}

export interface CustomFormatNumberOptions extends FormatNumberOptions {
   prefix?: string;
   suffix?: string;
}

export function mergeFormatOptions(specificFormatOptions: CustomFormatNumberOptions = {}, defaultFormatOptions: CustomFormatNumberOptions = {}) {
   const attributesToSkip = [];

   if (
      specificFormatOptions?.minimumFractionDigits !== defaultFormatOptions?.minimumFractionDigits ||
      specificFormatOptions?.maximumFractionDigits !== defaultFormatOptions?.maximumFractionDigits
   ) {
      attributesToSkip.push('minimumSignificantDigits');
      attributesToSkip.push('maximumSignificantDigits');
   }

   if (
      specificFormatOptions?.minimumSignificantDigits !== defaultFormatOptions?.minimumSignificantDigits ||
      specificFormatOptions?.maximumSignificantDigits !== defaultFormatOptions?.maximumSignificantDigits
   ) {
      attributesToSkip.push('minimumFractionDigits');
      attributesToSkip.push('maximumFractionDigits');
   }

   const specificFormatOptionsSanitized = Object.fromEntries(Object.entries(specificFormatOptions ?? {}).filter(([, value]) => value !== null));

   return merge(omit(defaultFormatOptions, [...attributesToSkip, ...Object.keys(specificFormatOptionsSanitized)]), specificFormatOptionsSanitized);
}

export function getDecimalSeparator(locale: Intl.LocalesArgument) {
   const number = 1.1;

   return (
      Intl.NumberFormat(locale)
         .formatToParts(number)
         .find((part) => part.type === 'decimal')?.value ?? ''
   );
}

export function getGroupingSeparator(locale: Intl.LocalesArgument) {
   const number = 10000;

   return (
      Intl.NumberFormat(locale)
         .formatToParts(number)
         .find((part) => part.type === 'group')?.value ?? ''
   );
}

export function formatNumber(intl: IntlShape, value: number, unit?: string, formatOptions?: CustomFormatNumberOptions | null, divisor = 100) {
   const sanitizedFormatOptions = Object.fromEntries(Object.entries(formatOptions ?? {}).filter(([, value]) => value !== null));

   const prefix = `${sanitizedFormatOptions?.prefix ?? ''}`;
   const _value = `${intl.formatNumber(sanitizedFormatOptions?.style === 'percent' ? value / divisor : value, {
      ...omit(sanitizedFormatOptions, ['prefix', 'suffix', 'style', 'unitDisplay', 'minimumSignificantDigits', 'maximumSignificantDigits']),
      style:
         sanitizedFormatOptions?.style === 'unit' && (!unit || !sanctionedUnits.includes(unit))
            ? 'decimal'
            : sanitizedFormatOptions?.style,
      unitDisplay: sanitizedFormatOptions?.unitDisplay ?? 'short',
   })}`;
   const separator = `${sanitizedFormatOptions?.style === 'unit' && unit && !sanctionedUnits.includes(unit) && (sanitizedFormatOptions?.unitDisplay ?? 'short') === 'short' ? ' ' : ''}`;
   const _unit = `${sanitizedFormatOptions?.style === 'unit' && unit && !sanctionedUnits.includes(unit) ? unit : ''}`;
   const suffix = `${sanitizedFormatOptions?.suffix ?? ''}`;

   return `${prefix}${_value}${separator}${_unit}${suffix}`;
}
